import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/Spinner';
import { afClubError, testModeExplanation, type AfFormDraft } from '../../lib/appointments/afCalendarForm';
import { checkAfClub, type AfCheckResult } from './api';

/**
 * The Anytime Fitness club-calendar fields: the club id (prefilled from the account's AF lead route by the parent), an
 * optional staff allowlist, test mode, and a CHECK button that runs ONE availability GET and shows the staff count and the
 * next free slot. Check is a read only: nothing is sent to Anytime Fitness beyond that GET.
 */
export function AfCalendarFields({
  value,
  onChange,
  accountId,
}: {
  value: AfFormDraft;
  onChange: (next: AfFormDraft) => void;
  accountId: string | null;
}) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<AfCheckResult | null>(null);
  const clubError = value.locationNumber.trim() === '' ? null : afClubError(value.locationNumber);

  const runCheck = async () => {
    setChecking(true);
    setResult(null);
    try {
      setResult(await checkAfClub(value.locationNumber.trim(), accountId));
    } catch (err) {
      setResult({
        ok: false,
        error: err instanceof Error ? err.message : 'The check failed.',
        staffCount: 0,
        slotCount: 0,
        nextSlot: null,
        timezone: null,
        problems: [],
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="af-calendar-fields">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="af-club">Anytime Fitness club id</Label>
          <Input
            id="af-club"
            className="mt-1"
            value={value.locationNumber}
            onChange={(e) => onChange({ ...value, locationNumber: e.target.value })}
            placeholder="AU-1432"
          />
          {clubError ? (
            <p className="mt-1 text-xs text-destructive">{clubError}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Prefilled from this client&apos;s Anytime Fitness lead route when it has one.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="af-allowlist">Staff allowlist (optional)</Label>
          <Input
            id="af-allowlist"
            className="mt-1"
            value={value.allowlistText}
            onChange={(e) => onChange({ ...value, allowlistText: e.target.value })}
            placeholder="Anyone Anytime Fitness lists"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Anytime Fitness staff ids, comma separated. Leave empty to book with whoever has the time.
          </p>
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={value.testMode}
            onCheckedChange={(c) => onChange({ ...value, testMode: c === true })}
            aria-label="Test mode"
          />
          Test mode
        </label>
        <p
          className={`mt-1 text-xs ${
            value.testMode ? 'text-amber-700' : 'text-muted-foreground'
          }`}
        >
          {testModeExplanation(value)}
        </p>
      </div>

      <div>
        <Button
          type="button"
          variant="outline"
          disabled={checking || clubError !== null || value.locationNumber.trim() === ''}
          onClick={() => void runCheck()}
        >
          {checking && <Spinner className="mr-1 h-4 w-4" />}
          Check
        </Button>
        {result && (
          <p className={`mt-2 text-sm ${result.ok ? 'text-foreground' : 'text-destructive'}`} data-testid="af-check-result">
            {result.ok
              ? `${result.staffCount} staff, ${result.slotCount} bookable times in the next two weeks${
                  result.nextSlot
                    ? `. Next free: ${result.nextSlot.localStart.replace('T', ' ')}${
                        result.timezone ? ` (${result.timezone})` : ' (UTC: no Business Profile time zone)'
                      }.`
                    : '.'
                }`
              : result.error}
          </p>
        )}
      </div>
    </div>
  );
}
